import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { Aperture } from "@/components/Aperture";
import Reveal from "@/components/Reveal";
import PortfolioGrid from "@/components/PortfolioGrid";
import Marquee from "@/components/Marquee";
import { HERO_IMG, CITY_LIGHTS, SERVICES, STATS, PARTNERS, TESTIMONIALS, CITIES, PROCESS } from "@/lib/data";

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section className="relative min-h-screen overflow-hidden grain" data-testid="hero-section">
      {/* Background image with parallax */}
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_40%,rgba(0,229,255,0.10),transparent_45%)]" />
      </motion.div>

      {/* Aperture motif */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, ease: [0.2, 0.7, 0.2, 1] }}
        className="absolute right-[-160px] top-[14%] hidden md:block"
      >
        <Aperture size={620} />
      </motion.div>

      <div className="absolute inset-0 scan-line" />

      <div className="relative z-10 max-w-[1480px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-20">
        <Reveal>
          <div className="flex items-center gap-4 font-mono-tech text-[11px] tracking-[0.25em] uppercase text-white/55 mb-8">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <span>ATL · 33.7490°N</span>
            <span className="text-[var(--gold)]">/</span>
            <span>DC · 38.9072°N</span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h1 className="font-display text-[14vw] md:text-[9.5vw] leading-[0.88] tracking-[-0.04em] text-white max-w-[14ch]">
            Frames<br />
            <span className="font-serif-display text-[var(--gold)]">that</span> outlive<br />
            the moment.
          </h1>
        </Reveal>

        <Reveal delay={0.25}>
          <p className="mt-10 max-w-xl text-white/65 text-base md:text-lg leading-relaxed">
            Lucra is a cinematic photography studio built around one idea — show up, capture real moments, print &amp;
            frame on-site, and let guests walk out holding the memory. Based in Atlanta &amp; Washington, D.C.
          </p>
        </Reveal>

        <Reveal delay={0.4}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-gold" data-testid="hero-cta-primary">
              <span>Start a Project</span>
              <ArrowUpRight size={14} />
            </Link>
            <Link to="/portfolio" className="btn-ghost-light" data-testid="hero-cta-secondary">
              <span>View Portfolio</span>
            </Link>
            <Link to="/careers" className="btn-cyan" data-testid="hero-cta-careers">
              <span>Apply · Join the Team</span>
            </Link>
          </div>
        </Reveal>
      </div>

      {/* Bottom HUD strip */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/5 glass">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-5 flex flex-wrap items-center justify-between gap-4 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/55">
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-[var(--cyan)] animate-pulse" />
            <span>Booking · 2026 / 2027</span>
          </div>
          <div className="flex items-center gap-6">
            <span>F/1.4</span><span>ISO 100</span><span>50mm</span><span>SHUTTER 1/250</span>
          </div>
          <div className="flex items-center gap-2 text-white/75">
            <span>Scroll</span><ChevronDown size={12} className="animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
};

const StatsBand = () => (
  <section className="border-y border-white/5 bg-black">
    <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className={`py-10 md:py-14 ${i !== 0 ? "md:border-l border-white/5" : ""} px-2`}>
            <div className="font-display text-5xl md:text-6xl text-white tracking-tight">{s.value}</div>
            <div className="mt-3 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/45">{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Mission = () => (
  <section className="relative py-28 md:py-40" data-testid="mission-section">
    <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
      <Reveal className="md:col-span-3">
        <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)]">
          <span className="text-white/35 mr-3">02 /</span>Mission
        </div>
      </Reveal>
      <div className="md:col-span-9">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] text-white max-w-4xl">
            We capture meaningful moments in real time and translate them into
            <span className="text-[var(--gold)]"> visual masterpieces</span> — for guests,
            for venues, and for the people who will look at these frames in forty years.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 grid md:grid-cols-2 gap-x-12 gap-y-4 text-white/65 leading-relaxed">
            <p>
              We treat every project — whether a wedding, a brand campaign, or a quiet portrait session — as a single
              cinematic narrative. Light, motion, composition, and color are tools used in service of a story.
            </p>
            <p>
              And because we live between Atlanta and D.C., we move easily between rooftop summers and townhouse winters,
              between sound stages and Sunday mornings.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="relative py-28 md:py-36 border-t border-white/5" data-testid="process-section">
    <div className="max-w-[1480px] mx-auto px-6 md:px-10">
      <div className="grid md:grid-cols-12 gap-10 mb-16">
        <Reveal className="md:col-span-5">
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-4">
            <span className="text-white/35 mr-3">03 /</span>Signature Partnership Program
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] text-white">
            How it <span className="font-serif-display text-[var(--gold)]">works</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7 md:pt-8">
          <p className="text-white/65 leading-relaxed text-lg max-w-xl">
            Our flagship service for restaurants, lounges, nightclubs, hotels, and venues. A four-step on-site experience
            that turns ordinary nights into framed memories — and a new revenue moment — without any setup cost on your end.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
        {PROCESS.map((p, i) => (
          <Reveal key={p.tag} delay={i * 0.1}>
            <div className="relative bg-[#0a0a0a] p-8 md:p-10 h-full beam-card" data-testid={`process-step-${p.tag}`}>
              <div className="font-mono-tech text-xs text-[var(--cyan)] mb-6">{p.tag} · STEP</div>
              <div className="font-display text-3xl text-white tracking-tight">{p.title}</div>
              <p className="mt-4 text-white/60 leading-relaxed">{p.body}</p>
              {i < PROCESS.length - 1 && (
                <div className="hidden lg:block absolute right-[-12px] top-1/2 -translate-y-1/2 h-px w-6 bg-[var(--gold)]/40" />
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.4}>
        <div className="mt-14 flex flex-wrap gap-4">
          <Link to="/contact" className="btn-gold" data-testid="process-cta"><span>Partner with Lucra</span><ArrowUpRight size={14} /></Link>
          <Link to="/services" className="btn-ghost-light">See all services</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const ServicesTeaser = () => (
  <section className="relative py-28 md:py-36 border-t border-white/5" data-testid="services-teaser">
    <div className="max-w-[1480px] mx-auto px-6 md:px-10">
      <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
        <div>
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)]">
            <span className="text-white/35 mr-3">04 /</span>Services
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[1.02] text-white max-w-2xl">
            Beyond the venue. <span className="font-serif-display text-[var(--gold)]">Custom</span> productions.
          </h2>
        </div>
        <Link to="/services" className="btn-ghost-light">All Services <ArrowUpRight size={14} /></Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 2) * 0.1}>
            <Link to={`/services#${s.slug}`} className="group relative block bg-[#0a0a0a] p-8 md:p-10 beam-card" data-testid={`service-card-${s.slug}`}>
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono-tech text-xs text-white/40">{s.tag}</span>
                <ArrowUpRight className="text-white/40 group-hover:text-[var(--gold)] group-hover:rotate-12 transition" size={20} />
              </div>
              <div className="font-display text-3xl md:text-4xl tracking-tight text-white">{s.name}</div>
              <p className="mt-3 text-white/55 max-w-md">{s.short}</p>
              <div className="mt-8 flex items-center gap-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[var(--cyan)]">
                <span className="h-px w-8 bg-[var(--cyan)]/60" />
                Inquire for details
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PortfolioTeaser = () => (
  <section className="relative py-28 md:py-36 border-t border-white/5" data-testid="portfolio-teaser">
    <div className="max-w-[1480px] mx-auto px-6 md:px-10">
      <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
        <div>
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)]">
            <span className="text-white/35 mr-3">01 /</span>Selected Work
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[1.02] text-white max-w-2xl">
            From the <span className="font-serif-display text-[var(--gold)]">archive</span>.
          </h2>
        </div>
        <Link to="/portfolio" className="btn-ghost-light">Open archive <ArrowUpRight size={14} /></Link>
      </div>
      <PortfolioGrid limit={6} showFilters={false} />
    </div>
  </section>
);

const Cities = () => (
  <section className="relative py-28 md:py-36 border-t border-white/5 overflow-hidden" data-testid="cities-section">
    <div className="absolute inset-0 opacity-30">
      <img src={CITY_LIGHTS} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
    </div>
    <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
      <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)]">
        <span className="text-white/35 mr-3">05 /</span>Two cities · One lens
      </div>
      <h2 className="mt-4 font-display text-5xl md:text-7xl tracking-tight leading-[1.0] text-white max-w-3xl">
        Atlanta <span className="text-white/30">×</span> D.C.
      </h2>

      <div className="mt-16 grid md:grid-cols-2 gap-10">
        {CITIES.map((c, i) => (
          <Reveal key={c.code} delay={i * 0.12}>
            <div className="glass p-8 md:p-10 beam-card relative overflow-hidden" data-testid={`city-card-${c.code.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="font-display text-6xl md:text-7xl tracking-tight text-white">{c.code}</div>
                <div className="text-right">
                  <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/50">{c.state}</div>
                  <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[var(--cyan)] mt-1">{c.coords}</div>
                </div>
              </div>
              <div className="font-display text-2xl text-white">{c.name}</div>
              <p className="mt-3 text-white/60 leading-relaxed">{c.blurb}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="relative py-28 md:py-36 border-t border-white/5" data-testid="testimonials-section">
    <div className="max-w-[1480px] mx-auto px-6 md:px-10">
      <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-12">
        <span className="text-white/35 mr-3">06 /</span>Voices
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <figure className="border border-white/10 p-8 h-full flex flex-col beam-card bg-[#0a0a0a]">
              <div className="font-display text-5xl text-[var(--gold)] leading-none">“</div>
              <blockquote className="mt-2 text-white/80 leading-relaxed flex-1">{t.quote}</blockquote>
              <figcaption className="mt-6 pt-6 border-t border-white/10">
                <div className="font-display text-lg text-white">{t.name}</div>
                <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/40 mt-1">{t.role}</div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const JoinTeam = () => (
  <section className="relative py-28 md:py-36 border-t border-white/5 overflow-hidden" data-testid="join-team-section">
    <div className="absolute -left-32 top-1/2 -translate-y-1/2 opacity-30">
      <Aperture size={460} />
    </div>
    <div className="relative max-w-[1480px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
      <Reveal className="md:col-span-7">
        <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6">
          <span className="text-white/35 mr-3">07 /</span>Careers
        </div>
        <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] text-white">
          Build with <span className="font-serif-display text-[var(--gold)]">Lucra</span>.
        </h2>
        <p className="mt-6 text-white/65 leading-relaxed max-w-xl text-lg">
          We are quietly growing the team. Photographers, editors, on-site assistants, and partnership coordinators who
          take craft seriously and care about the people in front of the lens.
        </p>
      </Reveal>
      <Reveal delay={0.15} className="md:col-span-5 md:text-right">
        <Link to="/careers" className="btn-gold" data-testid="home-careers-cta">
          <span>Apply · Join the Team</span><ArrowUpRight size={14} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const CTA = () => (
  <section className="relative py-28 md:py-40 border-t border-white/5 overflow-hidden" data-testid="home-cta-section">
    <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-50">
      <Aperture size={520} />
    </div>
    <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6">Let’s build something cinematic</div>
        <h2 className="font-display text-5xl md:text-8xl tracking-tight leading-[0.95] text-white max-w-4xl">
          Ready to be <span className="font-serif-display text-[var(--gold)]">remembered</span>?
        </h2>
        <p className="mt-8 text-white/65 max-w-xl text-lg">
          Tell us about your project. We respond to every inquiry within 48 hours, usually with a few thoughtful questions.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/contact" className="btn-gold" data-testid="home-cta-button"><span>Start a Project</span><ArrowUpRight size={14} /></Link>
          <a href="tel:+14705551427" className="btn-cyan">Call the studio</a>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Home = () => (
  <>
    <Hero />
    <PortfolioTeaser />
    <Marquee items={PARTNERS} />
    <StatsBand />
    <Mission />
    <Process />
    <ServicesTeaser />
    <Cities />
    <Testimonials />
    <JoinTeam />
    <CTA />
  </>
);

export default Home;
